import React from "react";
import { useSectors } from "newui/store/common-types";
import { SelectWithLabel, BaseSelectWithLabelProps, SelectOption } from "../InputWithLabel/SelectWithLabel";
import { useFormikFieldState } from "./useFormikField";

interface SectorSelectProps
  extends Omit<BaseSelectWithLabelProps, "options" | "isLoading"> {
  byCompanyId?: number;
  /** Maximum number of sectors that can be selected */
  maxSelect?: number;
  selectedIds?: number[];
}

export const SectorSelect: React.FC<SectorSelectProps> = ({
  label = "Sectors",
  placeholder = "Select sector",
  byCompanyId,
  selectedIds,
  maxSelect,
  value: valueProp,
  hasError: hasErrorProp,
  errorText: errorTextProp,
  onChange: onChangeProp,
  name,
  ...rest
}) => {
  const { values, isLoading } = useSectors(byCompanyId);
  const formik = useFormikFieldState(name);

  const resolvedValue = selectedIds && !isLoading
    ? selectedIds
      .map((id) => values.find((s) => s.value === id))
      .filter(Boolean) as SelectOption[]
    : selectedIds && isLoading
      ? undefined
      : valueProp;

  const currentCount = Array.isArray(resolvedValue) ? resolvedValue.length : 0;
  const isAtMax = maxSelect !== undefined && currentCount >= maxSelect;

  const handleChange = (selected: SelectOption | SelectOption[] | null) => {
    if (maxSelect !== undefined && Array.isArray(selected) && selected.length > maxSelect) {
      return;
    }
    formik.markTouched();
    onChangeProp?.(selected);
  };

  const resolvedPlaceholder = isAtMax
    ? `Maximum ${maxSelect} selected`
    : placeholder;

  return (
    <SelectWithLabel
      options={isAtMax ? [] : values as SelectOption[]}
      noOptionsMessage={() => isAtMax ? "Remove at least one item to make a selection" : "No options"}
      isLoading={isLoading}
      label={maxSelect ? `${label} (max ${maxSelect})` : label}
      placeholder={resolvedPlaceholder}
      value={resolvedValue}
      name={name}
      hasError={hasErrorProp || formik.hasError}
      errorText={errorTextProp || formik.errorText}
      onChange={handleChange}
      {...rest}
    />
  );
};
